'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Download, Upload, Check, FileText, KeyRound, Trash2, ChevronRight } from 'lucide-react';
import JSZip from 'jszip';
import SyncPanel from '@/components/SyncPanel';
import { useI18n, type Lang } from '@/lib/i18n';
import { db } from '@/lib/db';
import { BUILD_ID } from '@/lib/version';
import { getBlob, putBlob } from '@/lib/blobStore';
import { collectBlobHashes } from '@/lib/share/shareBundleCommon';
import { exportLogsNDJSON, clearLogs, getCurrentDeviceId, log } from '@/lib/log';
import { runBlobAudit, type BlobAuditReport } from '@/lib/blobAudit';
import { wipeAllData } from '@/lib/resetApp';
import { loadKey } from '@/lib/accountKey';
import { fetchMetrics } from '@/lib/metrics';
import { listLocalBackups, getBackupBlob, deleteBackup, maybeAutoBackup, getLastBackupTimestamp, type LocalBackupMeta } from '@/lib/localBackups';
import { useMeasurementSystem, setMeasurementSystem, type MeasurementSystem } from '@/lib/units';
import { ZineHeader } from '@/components/ui/ZineHeader';
import { splitZineTitle } from '@/lib/hooks';
import { Card } from '@/components/ui/Card';
import { ItalicBody } from '@/components/ui/ItalicBody';
import { ZineButton } from '@/components/ui/ZineButton';
import { Sheep } from '@/components/redesign/sheep-strip';
import RecoveryPhraseModal from '@/components/RecoveryPhraseModal';


// 2026-05-09 build 1182 — Veronika asked for the same B1 zine
// "TWO words / display caps + italic serif" headline that lives on
// the counter and profile pages. Inlined here so we don't need to
// touch the shared <Header> component (which is still used by all
// the other pages).

const fontSettingsDisplay = "var(--font-geist-sans), system-ui, -apple-system, sans-serif";
const fontSettingsSerif = "'Fraunces', 'Times New Roman', serif";

// 2026-05-09 build 1184 — B1 zine helpers from Veronika's
// SettingsPage.jsx mockup. SettingsSection headers each setting with
// a section number (§ I, § II, …) + display-caps name + optional
// italic-serif footnote. SegRow renders the segmented controls for
// language / measurement / theme as a single bordered row of black-
// on-paper2 buttons with the active option flipping to paper-on-ink.
function SettingsSection({ name, footnote, id, children }: { name: string; footnote?: string; id?: string; children: React.ReactNode }) {
  // 2026-05-09 build 1198 — Veronika: drop the underline rule
  // below each section name. The bordered box of the actual setting
  // already gives a clear visual break, so the underline is
  // redundant.
  return (
    <div id={id} style={id ? { scrollMarginTop: 72 } : undefined}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          // 2026-05-09 build 1200 — Veronika "even closer". Drop
          // the gap to zero. lineHeight: 1 on the title removes the
          // implicit text-leading that was opening the visual gap.
          paddingBottom: 0,
          marginBottom: 0,
          lineHeight: 1,
        }}
      >
        <div
          style={{
            fontFamily: fontSettingsDisplay,
            fontWeight: 800,
            fontSize: 18,
            letterSpacing: '-0.005em',
            textTransform: 'uppercase',
            color: 'var(--ink)',
            lineHeight: 1,
          }}
        >
          {name}
        </div>
      </div>
      {children}
      {footnote && (
        <div
          style={{
            fontFamily: fontSettingsSerif,
            fontStyle: 'italic',
            fontSize: 11,
            color: 'var(--ink-2)',
            // 2026-05-09 build 1202 — Veronika: italic explanations
            // can sit closer to their box. Trimmed marginTop 5 → 2.
            marginTop: 2,
            lineHeight: 1.4,
          }}
        >
          {footnote}
        </div>
      )}
    </div>
  );
}

type SegOption<T extends string> = { value: T; label: string; sub?: string };

function SegRow<T extends string>({ options, value, onChange }: { options: SegOption<T>[]; value: T; onChange: (v: T) => void }) {
  return (
    <Card style={{ display: 'flex' }}>
      {options.map((o, i) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              flex: 1,
              padding: '10px 6px',
              background: active ? 'var(--ink)' : 'transparent',
              color: active ? 'var(--paper-1)' : 'var(--ink)',
              border: 'none',
              borderLeft: i > 0 ? '1px solid var(--ink)' : 'none',
              fontFamily: fontSettingsDisplay,
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0,
              lineHeight: 1,
            }}
          >
            <span style={{ lineHeight: 1 }}>{o.label}</span>
            {o.sub && (
              // 2026-05-09 build 1197 — Veronika: switch the
              // unit-shorthand sub line to JetBrains Mono (the same
              // font as the "ENTRIES" caption on the home cards),
              // matching the "how to sync, backups…" subtitle below.
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 400,
                  fontSize: 8.5,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  opacity: 0.85,
                  marginTop: 2,
                  lineHeight: 1,
                }}
              >
                {o.sub}
              </span>
            )}
          </button>
        );
      })}
    </Card>
  );
}

export default function SettingsPage() {
  const router = useRouter();
  const { t, lang, setLang } = useI18n();
  const measurementSystem = useMeasurementSystem();
  const [status, setStatus] = useState<'idle' | 'exported' | 'imported'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deviceId, setDeviceId] = useState<string>('');
  useEffect(() => { setDeviceId(getCurrentDeviceId()); }, []);

  const handleLogDownload = async () => {
    const blob = await exportLogsNDJSON();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `knitlib-logs-${deviceId}-${new Date().toISOString().replace(/[:.]/g, '-')}.ndjson`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLogClear = async () => {
    if (!confirm(t.debugLogsClearConfirm)) return;
    await clearLogs();
  };

  // Photo storage audit — read-only walk of OPFS + IDB blob stores
  // that recomputes sha256 per stored blob and reports mismatches.
  // See `src/lib/blobAudit.ts` and PHOTO-RECOVERY-PLAN.md.
  const [auditStatus, setAuditStatus] = useState<'idle' | 'running' | 'done'>('idle');
  const [auditReport, setAuditReport] = useState<BlobAuditReport | null>(null);
  const handleBlobAudit = async () => {
    setAuditStatus('running');
    try {
      const report = await runBlobAudit();
      setAuditReport(report);
    } finally {
      setAuditStatus('done');
      setTimeout(() => setAuditStatus('idle'), 2000);
    }
  };

  // Phase 26.3: local auto-backup browser
  const [localBackups, setLocalBackups] = useState<LocalBackupMeta[]>([]);
  // Debug Logs + Automatic Local Backups are tucked behind a "For developer"
  // toggle so they don't clutter the main Settings screen (Sebastian
  // 2026-06-15). Collapsed by default; both stay fully functional.
  const [showDeveloper, setShowDeveloper] = useState(false);
  // Anonymous aggregate usage counters (developer section). Fetched lazily the
  // first time the section is expanded so opening Settings doesn't hit the relay.
  const [usage, setUsage] = useState<{ installs: number; synced: number } | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [lastBackupTs, setLastBackupTs] = useState<number | null>(null);
  // Whether this device already has an account key (= a recovery phrase
  // exists). Drives the DATA-section recovery-phrase button label.
  const [enrolled, setEnrolled] = useState(false);
  // Data-safety recovery-phrase reveal modal (build 1502).
  const [showPhraseModal, setShowPhraseModal] = useState(false);
  useEffect(() => { setEnrolled(loadKey() !== null); }, []);
  const refreshLocalBackups = async () => {
    setLocalBackups(await listLocalBackups());
    setLastBackupTs(getLastBackupTimestamp());
  };
  useEffect(() => { refreshLocalBackups(); }, []);
  // Load usage counters when the developer section first opens.
  useEffect(() => {
    if (!showDeveloper || usage !== null || usageLoading) return;
    setUsageLoading(true);
    fetchMetrics()
      .then((m) => { if (m) setUsage(m); })
      .finally(() => setUsageLoading(false));
  }, [showDeveloper, usage, usageLoading]);

  const handleBackupNow = async () => {
    // Force a write even if we're inside the rate-limit window by
    // clearing the last-backup timestamp first. Gives the user a
    // deterministic "I want a checkpoint right now" button.
    localStorage.removeItem('knitlib-last-local-backup');
    await maybeAutoBackup();
    await refreshLocalBackups();
  };

  const handleBackupRestore = async (ts: number) => {
    if (!confirm(t.localBackupRestoreConfirm)) return;
    const blob = await getBackupBlob(ts);
    if (!blob) return;
    await restoreFromBlob(blob);
    // Force a reload so every in-memory cache starts from the
    // restored state cleanly.
    window.location.replace('/');
  };

  const handleBackupDelete = async (ts: number) => {
    if (!confirm(t.localBackupDeleteConfirm)) return;
    await deleteBackup(ts);
    await refreshLocalBackups();
  };

  const handleResetApp = async () => {
    const first = confirm(t.resetAppConfirm1);
    if (!first) return;
    const second = confirm(t.resetAppConfirm2);
    if (!second) return;
    await wipeAllData();
    // Force a full reload so every in-memory cache (Automerge docs, React
    // state, service worker) starts from zero.
    window.location.replace('/');
  };

  const handleExport = async () => {
    log.info('app', 'export-start', {
      hasShare: typeof navigator !== 'undefined' && typeof navigator.share === 'function',
      hasCanShare: typeof navigator !== 'undefined' && typeof navigator.canShare === 'function',
      standalone: typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)')?.matches ?? false),
    });
    try {
      await runExport();
    } catch (err) {
      log.error('app', 'export-uncaught', {
        name: err instanceof Error ? err.name : '',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const runExport = async () => {
    const projects = await db.projects.toArray();
    log.info('app', 'export-step', { step: 'projects-read', n: projects.length });
    const yarns = await db.yarns.toArray();
    log.info('app', 'export-step', { step: 'yarns-read', n: yarns.length });
    const inventory = await db.inventory.toArray();
    log.info('app', 'export-step', { step: 'inventory-read', n: inventory.length });
    const techniques = await db.techniques.toArray();
    log.info('app', 'export-step', { step: 'techniques-read', n: techniques.length });
    const inspirations = await db.inspirations.toArray();
    log.info('app', 'export-step', { step: 'inspirations-read', n: inspirations.length });
    const counterSnapshots = await db.counterSnapshots.toArray();
    log.info('app', 'export-step', { step: 'counterSnapshots-read', n: counterSnapshots.length });
    const charts = await db.charts.toArray();
    log.info('app', 'export-step', { step: 'charts-read', n: charts.length });
    const timeSessions = await db.timeSessions.toArray();
    log.info('app', 'export-step', { step: 'timeSessions-read', n: timeSessions.length });

    const data = {
      version: 2,
      format: 'klib',
      exportedAt: new Date().toISOString(),
      projects,
      yarns,
      inventory,
      techniques,
      inspirations,
      counterSnapshots,
      charts,
      timeSessions,
    };

    // Gather every blob reference from any field in any row and embed the
    // bytes under `blobs/<hash>` in the ZIP bundle. This keeps the manifest
    // small and de-duplicates automatically (same photo used twice = one entry).
    const hashes = new Set<string>();
    collectBlobHashes(data, hashes);
    log.info('app', 'export-step', { step: 'hashes-collected', n: hashes.size });

    // Parallelize OPFS reads so we minimize the time between user gesture
    // and the eventual `navigator.share` call. iOS Safari requires
    // transient user activation for share() and a long await chain (33
    // sequential getBlob calls + generateAsync) was burning through the
    // gesture window before share() got a chance to fire.
    const blobResults = await Promise.all(
      [...hashes].map(async (hash) => ({ hash, blob: await getBlob(hash) })),
    );
    const resolved = blobResults.filter((r): r is { hash: string; blob: Blob } => r.blob != null);
    const missing = blobResults.filter(r => r.blob == null).map(r => r.hash);
    log.info('app', 'export-blobs-collected', {
      total: hashes.size,
      resolved: resolved.length,
      missing: missing.length,
    });

    // Skip the confirm() — it's a "warning" but in iOS PWA mode it can
    // burn user-activation context that we need for navigator.share()
    // a few hundred ms later. Always continue with whatever we have;
    // a partial export is still useful as a safety net.
    if (missing.length > 0) {
      log.warn('app', 'export-partial', { missingCount: missing.length });
    }

    const zip = new JSZip();
    zip.file('manifest.json', JSON.stringify(data, null, 2));
    const blobsDir = zip.folder('blobs');
    for (const { hash, blob } of resolved) {
      blobsDir?.file(hash, blob);
    }

    const bundle = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    const filename = `knitterslibrary-${new Date().toISOString().split('T')[0]}.klib`;
    log.info('app', 'export-zip-built', { bytes: bundle.size, filename });

    // iOS PWAs (added to home screen, standalone mode) silently drop
    // anchor-tag downloads — `a.click()` returns without showing a save
    // sheet or writing to Files. Veronika hit this on her iPad on
    // 2026-04-29 with build 1004. Use Web Share API with files which
    // works inside iOS PWAs and surfaces the native share sheet so the
    // user can pick "Save to Files", AirDrop, Mail, etc. Fall through
    // to anchor-tag path for desktop browsers.
    let exported = false;
    try {
      const file = new File([bundle], filename, { type: 'application/zip' });
      const canShareFiles =
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });
      log.info('app', 'export-share-attempt', { canShareFiles });
      if (canShareFiles) {
        await navigator.share({ files: [file], title: "Knitter's Library backup" });
        log.info('app', 'export-share-ok');
        exported = true;
      }
    } catch (err) {
      const errName = err instanceof Error ? err.name : '';
      const errMsg = err instanceof Error ? err.message : String(err);
      // User dismissed the share sheet — deliberate cancel, exit silently.
      if (errName === 'AbortError') {
        log.info('app', 'export-share-aborted');
        return;
      }
      // Other share errors → log and fall through to anchor.
      log.warn('app', 'export-share-failed', { name: errName, message: errMsg });
    }

    if (!exported) {
      log.info('app', 'export-anchor-fallback');
      const url = URL.createObjectURL(bundle);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
    setStatus('exported');
    setTimeout(() => setStatus('idle'), 2000);
    log.info('app', 'export-done', { exportedViaShare: exported });
  };

  /**
   * Core restore pipeline — accepts a Blob (the `.klib` bundle or legacy
   * JSON) and replaces the current library with its contents. Used by
   * both the manual "Import Data" file-input flow and the "Restore from
   * automatic backup" flow in Phase 26.3.
   */
  const restoreFromBlob = async (file: Blob) => {
    const buf = new Uint8Array(await file.arrayBuffer());
    // Sniff the first bytes to distinguish a ZIP (klib bundle, v2) from a
    // legacy JSON export (v1). `PK\x03\x04` is the ZIP local-file header.
    const isZip = buf.length >= 4 && buf[0] === 0x50 && buf[1] === 0x4b && buf[2] === 0x03 && buf[3] === 0x04;

    let data: {
      projects?: unknown[];
      /** Legacy field from .klib bundles exported before the diary feature
       *  was removed. Silently ignored on import. */
      diaryEntries?: unknown[];
      yarns?: unknown[];
      inventory?: unknown[];
      techniques?: unknown[];
      inspirations?: unknown[];
      counterSnapshots?: unknown[];
      charts?: unknown[];
      timeSessions?: unknown[];
    };

    if (isZip) {
      const zip = await JSZip.loadAsync(buf);
      const manifest = zip.file('manifest.json');
      if (!manifest) throw new Error('Missing manifest.json in .klib bundle');
      data = JSON.parse(await manifest.async('string'));

      // Re-import every blob from blobs/<hash>. Content-addressed means if
      // the hash already exists locally we skip writing (putBlob dedupes by
      // hash after storing, but we save a write by checking first).
      const blobsDir = zip.folder('blobs');
      if (blobsDir) {
        const entries: { name: string; file: JSZip.JSZipObject }[] = [];
        blobsDir.forEach((relativePath, f) => {
          if (!f.dir) entries.push({ name: relativePath, file: f });
        });
        for (const { file: f } of entries) {
          const bytes = await f.async('uint8array');
          await putBlob(bytes);
        }
      }
    } else {
      // Legacy v1 JSON export — photos are still base64 `data:` URLs.
      // The blob migration on next app load will rewrite them in place.
      data = JSON.parse(new TextDecoder().decode(buf));
    }

    await db.transaction('rw', [db.projects, db.yarns, db.inventory, db.techniques, db.inspirations, db.counterSnapshots, db.charts, db.timeSessions], async () => {
      await Promise.all([
        db.projects.clear(),
        db.yarns.clear(),
        db.inventory.clear(),
        db.techniques.clear(),
        db.inspirations.clear(),
        db.counterSnapshots.clear(),
        db.charts.clear(),
        db.timeSessions.clear(),
      ]);
      if (data.projects?.length) await db.projects.bulkAdd(data.projects as never[]);
      // data.diaryEntries is silently dropped — the diary feature was removed.
      if (data.yarns?.length) await db.yarns.bulkAdd(data.yarns as never[]);
      if (data.inventory?.length) await db.inventory.bulkAdd(data.inventory as never[]);
      if (data.techniques?.length) await db.techniques.bulkAdd(data.techniques as never[]);
      if (data.inspirations?.length) await db.inspirations.bulkAdd(data.inspirations as never[]);
      if (data.counterSnapshots?.length) await db.counterSnapshots.bulkAdd(data.counterSnapshots as never[]);
      if (data.timeSessions?.length) await db.timeSessions.bulkAdd(data.timeSessions as never[]);
      if (data.charts?.length) await db.charts.bulkAdd(data.charts as never[]);
    });

    // Reset the migration flag so a legacy JSON import gets its base64
    // photos converted on next boot.
    if (!isZip) localStorage.removeItem('knitlib-blob-migration-v1');

    setStatus('imported');
    setTimeout(() => setStatus('idle'), 2000);
  };

  /**
   * Restore from the *unpacked* contents of a `.klib` bundle.
   *
   * iOS Safari aggressively auto-extracts any downloaded or AirDropped
   * file that looks like a ZIP, saving the archive's contents as loose
   * files in iCloud / Files (a `manifest.json` plus a `blobs/` folder).
   * The system file picker then only lets the user pick one file at
   * a time. Users who try to import on iPhone end up with just
   * `manifest.json` selected, silently losing every photo.
   *
   * This path accepts a multi-selection (`<input multiple>`) so the
   * user can pick `manifest.json` + every file inside `blobs/` at
   * once, and we reassemble the bundle. Blob file names don't matter
   * — `putBlob` is content-addressed — we just feed each non-manifest
   * file in as bytes.
   */
  const restoreFromFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    const manifestFile = arr.find(f => {
      const name = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
      return name.endsWith('manifest.json') || name === 'manifest.json';
    });
    if (!manifestFile) {
      throw new Error('Missing manifest.json. Select the manifest.json file plus every file inside the blobs folder.');
    }

    const blobFiles = arr.filter(f => f !== manifestFile);
    for (const bf of blobFiles) {
      const bytes = new Uint8Array(await bf.arrayBuffer());
      await putBlob(bytes);
    }

    const data = JSON.parse(await manifestFile.text()) as {
      projects?: unknown[];
      diaryEntries?: unknown[];
      yarns?: unknown[];
      inventory?: unknown[];
      techniques?: unknown[];
      inspirations?: unknown[];
      counterSnapshots?: unknown[];
      charts?: unknown[];
      timeSessions?: unknown[];
    };

    await db.transaction('rw', [db.projects, db.yarns, db.inventory, db.techniques, db.inspirations, db.counterSnapshots, db.charts, db.timeSessions], async () => {
      await Promise.all([
        db.projects.clear(),
        db.yarns.clear(),
        db.inventory.clear(),
        db.techniques.clear(),
        db.inspirations.clear(),
        db.counterSnapshots.clear(),
        db.charts.clear(),
        db.timeSessions.clear(),
      ]);
      if (data.projects?.length) await db.projects.bulkAdd(data.projects as never[]);
      if (data.yarns?.length) await db.yarns.bulkAdd(data.yarns as never[]);
      if (data.inventory?.length) await db.inventory.bulkAdd(data.inventory as never[]);
      if (data.techniques?.length) await db.techniques.bulkAdd(data.techniques as never[]);
      if (data.inspirations?.length) await db.inspirations.bulkAdd(data.inspirations as never[]);
      if (data.counterSnapshots?.length) await db.counterSnapshots.bulkAdd(data.counterSnapshots as never[]);
      if (data.charts?.length) await db.charts.bulkAdd(data.charts as never[]);
      if (data.timeSessions?.length) await db.timeSessions.bulkAdd(data.timeSessions as never[]);
    });

    setStatus('imported');
    setTimeout(() => setStatus('idle'), 2000);
  };

  /** Thin file-input wrapper. Dispatches to the correct restore path:
   *  - exactly one file → `restoreFromBlob` (handles .klib ZIP + legacy JSON)
   *  - multiple files   → `restoreFromFiles` (iOS-extracted bundle case)
   *
   *  Any error is surfaced to the user as an alert instead of bubbling
   *  to the React error boundary — the import UI has no error chrome
   *  of its own yet, and a runtime crash is worse than a clear string. */
  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (!confirm(t.importConfirm)) {
      e.target.value = '';
      return;
    }
    try {
      if (files.length === 1) {
        await restoreFromBlob(files[0]);
      } else {
        await restoreFromFiles(files);
      }
    } catch (err) {
      alert(`Import failed:\n\n${(err as Error).message}`);
    } finally {
      e.target.value = '';
    }
  };

  const { head: titleHead, tail: titleTail } = splitZineTitle(t.settingsTitle);

  return (
    <>
      {/* B1 zine header — shared <ZineHeader> primitive. */}
      <ZineHeader
        head={titleHead}
        tail={titleTail}
        titleStyle={{ lineHeight: 1 }}
        onBack={() => router.back()}
      />
      <div className="px-5 py-4" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Language */}
        <SettingsSection name={t.language}>
          <SegRow
            options={[
              { value: 'en' as Lang, label: 'English' },
              { value: 'cs' as Lang, label: 'Čeština' },
            ]}
            value={lang}
            onChange={setLang}
          />
        </SettingsSection>

        {/* Measurement */}
        <SettingsSection name={t.measurementSystemLabel} footnote={t.measurementHint}>
          <SegRow
            options={[
              { value: 'metric' as MeasurementSystem, label: t.measurementMetric, sub: 'mm, m, per 10 cm' },
              { value: 'imperial' as MeasurementSystem, label: t.measurementImperial, sub: 'in, yd, per 4 in' },
            ]}
            value={measurementSystem}
            onChange={setMeasurementSystem}
          />
        </SettingsSection>

        {/* Appearance picker removed in build 1475 (Veronika msg
            4207-4211) — app is light-only by design. */}

        {/* User manual — 2026-05-09 build 1196 — Veronika asked to
            move this right under Appearance (now-removed) and dress
            it up to match the other settings "panels" (Language,
            Measurement). Renders as a SettingsSection with a single
            paper2 bordered row inside that links to /manual. */}
        <SettingsSection name={t.userManualTitle}>
          <Link
            href="/manual"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              border: '1px solid var(--ink)',
              background: 'var(--paper-2)',
              padding: '10px 12px',
              textDecoration: 'none',
              color: 'var(--ink)',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--ink-2)',
                lineHeight: 1.3,
              }}
            >
              {t.userManualLinkLabel}
            </span>
            <span
              aria-hidden
              style={{
                fontFamily: fontSettingsDisplay,
                fontWeight: 800,
                fontSize: 18,
                lineHeight: 1,
                color: 'var(--ink)',
                flexShrink: 0,
              }}
            >
              →
            </span>
          </Link>
          {/* Replay the first-run tour (Sebastian 2026-06-08). One-shot
              sessionStorage flag the home page reads to force-show it. */}
          <button
            type="button"
            data-testid="settings-replay-tour"
            onClick={() => { sessionStorage.setItem('knitlib-replay-tour', '1'); router.push('/'); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              width: '100%',
              marginTop: 8,
              border: '1px solid var(--ink)',
              background: 'var(--paper-2)',
              padding: '10px 12px',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--ink-2)',
                lineHeight: 1.3,
              }}
            >
              {t.tourReplay}
            </span>
            <span
              aria-hidden
              style={{
                fontFamily: fontSettingsDisplay,
                fontWeight: 800,
                fontSize: 16,
                lineHeight: 1,
                color: 'var(--ink)',
                flexShrink: 0,
              }}
            >
              ↻
            </span>
          </button>
          {/* Tip jar (Veronika 2026-06-12): a quiet, voluntary thank-you
              link to her Ko-fi page — never a popup, never a feature gate,
              no tracking (it's a plain outbound link; payment happens
              entirely on Ko-fi/Stripe, so the privacy story is untouched).
              Accent border marks it as the one "support us" affordance. */}
          {/* Tip-jar card (Veronika msgs 4872 + 4878): tinted accent wash +
              2px accent border, and an explicit SUPPORT US ZineButton — the
              whole-row-as-link version read as plain text, nobody knew it
              was tappable. Solid-orange card fill was considered and
              rejected: white Fraunces italic at this size on #FB5E00 is
              below comfortable contrast. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              // Veronika msg 4892: clear daylight between the tour button
              // and the support card so they don't read as one block.
              marginTop: 24,
              border: '2px solid var(--accent)',
              background: 'rgba(251, 94, 0, 0.10)',
              padding: '12px 12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--accent)',
                  lineHeight: 1.3,
                }}
              >
                {t.tipJarLabel}
              </span>
              {/* The app's pixel sheep, standing in place and grazing
                  (Veronika msg 4886 — replaced the 🧶 emoji). Head dips
                  down and back up via the same .sheep-head group the
                  meadow strip animates; body stays still. */}
              <span className="tip-jar-sheep" aria-hidden style={{ flexShrink: 0, display: 'flex' }}>
                <Sheep width={32} />
              </span>
              <style>{`
                .tip-jar-sheep .sheep-head {
                  animation: tip-jar-graze 3.2s ease-in-out infinite;
                  transform-origin: 6px 12px;
                }
                @keyframes tip-jar-graze {
                  0%, 55%, 100% { transform: translateY(0) rotate(0deg); }
                  65%, 90%      { transform: translateY(2px) rotate(8deg); }
                }
                @media (prefers-reduced-motion: reduce) {
                  .tip-jar-sheep .sheep-head { animation: none; }
                }
              `}</style>
            </div>
            <ItalicBody style={{ fontSize: 11, lineHeight: 1.45 }}>
              {t.tipJarBlurb}
            </ItalicBody>
            <ZineButton
              fullWidth
              data-testid="settings-tip-jar"
              onClick={() => window.open('https://ko-fi.com/knitterslibrary', '_blank', 'noopener,noreferrer')}
            >
              {t.tipJarButton}
            </ZineButton>
          </div>
        </SettingsSection>

        {/* Data safety — recovery phrase + export/import. Moved ABOVE
            Sync in build 1501 (Sebastian, Telegram 2026-05-22): a new
            user should secure their OWN device first — save the 12
            words, build the library, export backups — and only then
            optionally sync a second device. Opens with a plain-language
            explainer of the three things that keep a library safe
            (build 1502) because a tester couldn't tell from this screen
            what actually keeps her data safe. */}
        <SettingsSection name={t.data}>
        <div className="p-4 space-y-3" style={{ background: 'var(--paper-2)', border: '1px solid var(--ink)' }}>
          {/* Plain-language explainer: the three things that keep a
              library safe, and why a user does each (Sebastian, Telegram
              2026-05-22 — a tester couldn't tell why she'd save 12 words
              AND export AND have automatic backups). Each row: emoji +
              bold name + what-it-is/why. The automatic-backups controls
              stay in their own section lower down — this only explains
              them; the user doesn't touch them unless something breaks. */}
          <div style={{ padding: '8px 10px', background: 'var(--paper-1)', border: '1px solid var(--ink)' }}>
            <p className="text-xs" style={{ color: 'var(--ink)', lineHeight: 1.45, margin: '0 0 8px' }}>
              {t.dataSafetyIntro}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { icon: '🛟', name: t.dataSafetyBackupsName, desc: t.dataSafetyBackupsDesc },
                { icon: '💾', name: t.dataSafetyExportName, desc: t.dataSafetyExportDesc },
                { icon: '🔑', name: t.dataSafetyPhraseName, desc: t.dataSafetyPhraseDesc },
              ].map((m) => (
                <div key={m.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span aria-hidden style={{ fontSize: 14, lineHeight: 1.4, flexShrink: 0 }}>{m.icon}</span>
                  <p className="text-xs" style={{ color: 'var(--ink)', lineHeight: 1.45, margin: 0 }}>
                    <strong>{m.name}</strong> — {m.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Recovery phrase — step 1's action. The 12 words are a
              single-device user's safety net too, not just a sync
              feature (Veronika, Telegram 2026-05-20). The button scrolls
              down to the Sync section, the one secure spot where the
              phrase is actually revealed. */}
          <div style={{ padding: '8px 10px', background: 'var(--paper-1)', border: '1px solid var(--ink)' }}>
            <p className="text-xs" style={{ color: 'var(--ink)', lineHeight: 1.45, margin: '0 0 8px' }}>
              {t.dataRecoveryIntro}
            </p>
            <ZineButton
              variant="solid"
              size="small"
              onClick={() => {
                // Enrolled: reveal the 12 words right here in a pop-up.
                // Not enrolled yet: there is no phrase to show — send the
                // user to the Sync section where the create-phrase flow
                // lives. (Build 1502 — Sebastian: the button should show
                // the phrase, not just scroll to another button.)
                if (enrolled) {
                  setShowPhraseModal(true);
                } else {
                  document.getElementById('settings-sync-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              data-testid="data-recovery-phrase-button"
              style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <KeyRound size={12} aria-hidden />
              {enrolled ? t.syncBtnShowPhrase : t.dataRecoveryCreateBtn}
            </ZineButton>
          </div>

          <ZineButton
            variant="solid"
            onClick={handleExport}
            style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {status === 'exported' ? <Check size={14} aria-hidden /> : <Download size={14} aria-hidden />}
            {status === 'exported' ? t.exportSuccess : t.exportData}
          </ZineButton>
          <p className="text-[10px] text-text-tertiary -mt-1">{t.exportDataHint}</p>

          <ZineButton
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {status === 'imported' ? <Check size={14} aria-hidden /> : <Upload size={14} aria-hidden />}
            {status === 'imported' ? t.importSuccess : t.importData}
          </ZineButton>
          <p className="text-[10px] text-text-tertiary -mt-1">{t.importDataHint}</p>
          {/* Accept either a single .klib / .json file OR a multi-selection
              of the extracted archive's files (iOS Safari auto-extracts
              .klib during download/AirDrop). The import handler branches
              on the number of files. */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleImport}
            className="hidden"
          />
        </div>
        </SettingsSection>

        {/* Sync — moved BELOW Data safety in build 1501: syncing a
            second device is optional and comes after a user has
            secured their own. 2026-05-09 build 1203 — framed like the
            User Manual link: 1px ink border around a paper2 box that
            holds the panel's internals. */}
        <SettingsSection name="Sync" id="settings-sync-section" footnote={t.syncIsTransport}>
          <div
            style={{
              border: '1px solid var(--ink)',
              background: 'var(--paper-2)',
              padding: '8px 10px',
            }}
          >
            <SyncPanel />
          </div>
        </SettingsSection>

        {/* For developer — Debug Logs + Automatic Local Backups, tucked behind
            a toggle so they don't clutter the main Settings screen (Sebastian
            2026-06-15). Both stay fully functional, just collapsed by default. */}
        <div>
          <button
            type="button"
            onClick={() => setShowDeveloper((v) => !v)}
            data-testid="settings-developer-toggle"
            aria-expanded={showDeveloper}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: fontSettingsDisplay,
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: '-0.005em',
              lineHeight: 1,
              color: 'var(--ink)',
              textAlign: 'left',
            }}
          >
            <ChevronRight
              size={18}
              strokeWidth={2.5}
              aria-hidden
              style={{
                flexShrink: 0,
                transition: 'transform 120ms',
                transform: showDeveloper ? 'rotate(90deg)' : 'none',
              }}
            />
            {t.developerToolsTitle}
          </button>
          {showDeveloper && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 16 }}>
        {/* Usage counters — anonymous aggregate totals (Veronika 2026-06-17,
            "only info for us"). No identifiers; just the relay's running totals. */}
        <SettingsSection name={t.usageStatsTitle}>
        <div className="p-4 space-y-3" style={{ background: 'var(--paper-2)', border: '1px solid var(--ink)' }}>
          <p className="text-xs text-text-tertiary leading-relaxed">{t.usageStatsHint}</p>
          {usage ? (
            <div className="font-mono text-sm" style={{ color: 'var(--ink)' }}>
              <div>{t.usageStatsInstalls}: <strong>{usage.installs}</strong></div>
              <div>{t.usageStatsSynced}: <strong>{usage.synced}</strong></div>
            </div>
          ) : (
            <p className="text-[10px] font-mono text-text-tertiary">
              {usageLoading ? '…' : t.usageStatsUnavailable}
            </p>
          )}
        </div>
        </SettingsSection>

        {/* Debug logs */}
        <SettingsSection name={t.debugLogsTitle}>
        <div className="p-4 space-y-3" style={{ background: 'var(--paper-2)', border: '1px solid var(--ink)' }}>
          <p className="text-xs text-text-tertiary">{t.debugLogsHint}</p>
          <p className="text-[10px] font-mono text-text-tertiary">{t.debugLogsDeviceLabel} {deviceId || '…'}</p>
          <ZineButton
            variant="outlined"
            size="small"
            onClick={handleLogDownload}
            style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <FileText size={12} aria-hidden />
            {t.debugLogsDownload}
          </ZineButton>
          <ZineButton
            variant="outlined"
            size="small"
            onClick={handleLogClear}
            style={{
              width: '100%',
              flex: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              color: 'var(--ink-2)',
              borderColor: 'var(--ink-2)',
            }}
          >
            <Trash2 size={12} aria-hidden />
            {t.debugLogsClear}
          </ZineButton>

          {/* Photo storage audit — read-only walk over the local blob
              backends (OPFS + IDB) that recomputes sha256 per blob and
              cross-walks Dexie refs. Reports go to VictoriaLogs as
              `blob-audit-*` events; the on-screen counts are a quick
              health summary. Surfaced for Veronika so I can ask her to
              tap it on iPad/iPhone without her doing anything else. */}
          <ZineButton
            variant="outlined"
            size="small"
            onClick={handleBlobAudit}
            disabled={auditStatus === 'running'}
            style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {auditStatus === 'done' ? <Check size={12} aria-hidden /> : <FileText size={12} aria-hidden />}
            {auditStatus === 'running' ? (t.blobAuditRunning ?? 'Running…') : (t.blobAuditButton ?? 'Run photo storage audit')}
          </ZineButton>
          {auditReport && (
            <p className="text-[10px] font-mono text-text-tertiary -mt-1">
              {auditReport.verified}/{auditReport.total} ok
              {' · '}{auditReport.mismatched} mismatch
              {' · '}{auditReport.orphaned} orphan
              {' · '}{auditReport.missingRefs} missing
              {' · '}{auditReport.durationMs}ms
            </p>
          )}
        </div>
        </SettingsSection>

        {/* Local automatic backups */}
        <SettingsSection name={t.localBackupsTitle}>
        <div className="p-4 space-y-3" style={{ background: 'var(--paper-2)', border: '1px solid var(--ink)' }}>
          <p className="text-xs text-text-tertiary leading-relaxed">{t.localBackupsHint}</p>

          {/* Last-backup status — colour-coded so a user with an
              unreliable auto-backup cadence (iOS PWA Safari can evict
              the backup IDB or fail to fire visibilitychange) notices
              at a glance that something is wrong. */}
          {(() => {
            const statusRowStyle = {
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '8px 10px',
              fontSize: 11,
              lineHeight: 1.45,
              color: 'var(--ink)',
              background: 'var(--paper-1)',
              border: '1px solid var(--ink)',
            } as const;
            if (lastBackupTs == null) {
              return (
                <div style={statusRowStyle}>
                  <span style={{ marginTop: 1 }}>⚠️</span>
                  <span>{t.lastBackupNone}</span>
                </div>
              );
            }
            const ageMs = Date.now() - lastBackupTs;
            const ageH = ageMs / (60 * 60 * 1000);
            const icon =
              ageH < 1.5 ? '✅'
              : ageH < 24 ? '⏳'
              : '⚠️';
            const whenStr = new Date(lastBackupTs).toLocaleString();
            const relStr =
              ageMs < 60_000 ? t.relNow
              : ageMs < 60 * 60_000 ? (t.relMinutesAgo ?? '{n} min ago').replace('{n}', String(Math.round(ageMs / 60_000)))
              : ageMs < 24 * 60 * 60_000 ? (t.relHoursAgo ?? '{n} h ago').replace('{n}', String(Math.round(ageH)))
              : (t.relDaysAgo ?? '{n} d ago').replace('{n}', String(Math.round(ageH / 24)));
            return (
              <div style={statusRowStyle}>
                <span style={{ marginTop: 1 }}>{icon}</span>
                <span>
                  {(t.lastBackupLabel ?? 'Last backup')}: {relStr}
                  <span style={{ opacity: 0.7, marginLeft: 4 }}>({whenStr})</span>
                </span>
              </div>
            );
          })()}

          <ZineButton
            variant="outlined"
            onClick={handleBackupNow}
            style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {t.localBackupsCreateNow}
          </ZineButton>
          {localBackups.length === 0 ? (
            <p className="text-[10px] text-text-tertiary italic text-center py-2">{t.localBackupsEmpty}</p>
          ) : (
            <ul style={{ display: 'flex', flexDirection: 'column', gap: 6, listStyle: 'none', padding: 0, margin: 0 }}>
              {localBackups.map(b => (
                <li
                  key={b.ts}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: 8,
                    background: 'var(--paper-1)',
                    border: '1px solid var(--ink)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {new Date(b.ts).toLocaleString()}
                    </p>
                    <p style={{ margin: 0, fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>
                      {(b.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <ZineButton
                    variant="outlined"
                    onClick={() => handleBackupRestore(b.ts)}
                    style={{ flex: 'none', padding: '6px 10px', fontSize: 10 }}
                  >
                    {t.localBackupsRestore}
                  </ZineButton>
                  <ZineButton
                    variant="danger"
                    icon
                    onClick={() => handleBackupDelete(b.ts)}
                    aria-label={t.delete}
                    style={{ width: 30, height: 30 }}
                  >
                    <Trash2 size={12} aria-hidden />
                  </ZineButton>
                </li>
              ))}
            </ul>
          )}
        </div>
        </SettingsSection>
            </div>
          )}
        </div>

        {/* Danger zone: reset everything */}
        <SettingsSection name={t.resetAppTitle}>
        <div className="p-4 space-y-3" style={{ background: 'var(--paper-2)', border: '1px solid var(--ink)' }}>
          <p className="text-xs text-text-tertiary leading-relaxed">{t.resetAppHint}</p>
          <ZineButton
            variant="danger"
            size="small"
            onClick={handleResetApp}
            style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <Trash2 size={12} aria-hidden />
            {t.resetAppBtn}
          </ZineButton>
        </div>
        </SettingsSection>

        <SettingsSection name={t.appInfo}>
        <div
          style={{
            background: 'var(--paper-2)',
            border: '1px solid var(--ink)',
            padding: 16,
            fontFamily: 'var(--font-mono)',
            color: 'var(--ink)',
            textAlign: 'right',
          }}
        >
          <p style={{ margin: 0, fontSize: 12, fontWeight: 500, letterSpacing: '0.04em' }}>
            Knitter&rsquo;s Library v0.1.0
          </p>
          <p style={{ margin: '2px 0 0 0', fontSize: 11, fontWeight: 500, letterSpacing: '0.06em' }}>
            build <span style={{ fontWeight: 800 }}>b{BUILD_ID}</span>
            {process.env.NODE_ENV === 'development' && (
              <span style={{ marginLeft: 4, fontWeight: 800, color: 'var(--over)' }}>-dev</span>
            )}
          </p>
        </div>
        {/* Contact us (Veronika msg 4894) — opens the marketing site's
            message form (the #feedback band on the homepage, which
            emails us). Language-aware: Czech app → Czech page. */}
        <a
          href={lang === 'cs' ? 'https://www.knitterslibrary.com/cs/#feedback' : 'https://www.knitterslibrary.com/#feedback'}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="settings-contact-us"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 8,
            border: '1px solid var(--ink)',
            background: 'var(--paper-2)',
            padding: '10px 12px',
            textDecoration: 'none',
            color: 'var(--ink)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--ink-2)',
              lineHeight: 1.3,
            }}
          >
            {t.contactUs}
          </span>
          <span
            aria-hidden
            style={{
              fontFamily: fontSettingsDisplay,
              fontWeight: 800,
              fontSize: 18,
              lineHeight: 1,
              color: 'var(--ink)',
              flexShrink: 0,
            }}
          >
            →
          </span>
        </a>
        </SettingsSection>

      </div>

      {/* Recovery-phrase reveal — opened from the Data safety section's
          "Show my recovery phrase" button (build 1502). */}
      <RecoveryPhraseModal open={showPhraseModal} onClose={() => setShowPhraseModal(false)} />
    </>
  );
}
